import { v4 as uuid } from 'uuid';
import { useEffect, useState } from 'react';
import {
  Box as Item,
  FormLabel,
  Button,
  Typography,
} from '@mui/material';
import Plan from '../../../../models/Plan/Plan';
import StatusStyle from './StatusStyle';
import ModalComponent from '../../../../components/ModalComponent';
import { useUserContext } from '../../../../contexts/UserContext';

// Stripe status to Japanese mapping
const stripeStatusToJapanese: { [key: string]: string } = {
  active: '有効',
  trialing: 'トライアル中',
  past_due: '支払い遅延',
  canceled: 'キャンセル済み',
  incomplete: '未完了',
  incomplete_expired: '期限切れ',
  unpaid: '未払い',
  paused: '一時停止中',
};

function PlanCard({
  plan,
  total,
  setTriedToRegisterToPlan,
  setSelectedPlan,
  setActivePlan,
  customOnClickEventFor,
  singlePagesCount,
}: {
  plan: Plan,
  total: number,
  setTriedToRegisterToPlan: (tried: boolean) => void,
  setSelectedPlan: (plan: Plan) => void,
  setActivePlan: (plan: Plan) => void,
  customOnClickEventFor?: (plan: Plan) => void,
  singlePagesCount?: number,
}) {
  // variables
  const [cardStyle, setCardStyle] = useState({});
  const [displayMessage, setDisplayMessage] = useState('');
  const [isDeactivated, setIsDeactivated] = useState(false);
  const [showCantReturnModal, setShowCantReturnModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const { user } = useUserContext();

  const invoicePlanId = process.env.REACT_APP_INVOICE_PLAN_ID ?? null;

  // effects
  useEffect(() => {
    if (user.is_invoice_mode_enabled) {
      if (plan.id === invoicePlanId) {
        setCardStyle(StatusStyle.isActivePlan);

        // Show status in Japanese if not active or trialing
        if (plan.stripe_status && plan.stripe_status !== 'active' && plan.stripe_status !== 'trialing') {
          const statusInJapanese = stripeStatusToJapanese[plan.stripe_status] || plan.stripe_status;
          setDisplayMessage(`利用中 (${statusInJapanese})`);
        } else {
          setDisplayMessage('利用中');
        }

        setIsDeactivated(true);
        setActivePlan(plan);
        return;
      }

      setModalContent('請求書モードが有効なため、プラン変更ができません。');
      setCardStyle(StatusStyle.isLowerPlan);
      setDisplayMessage('選択不可');
      setIsDeactivated(true);
      return;
    }

    // Add check for free plan with single pages
    if (plan.name === 'フリープラン' && singlePagesCount > 0) {
      setCardStyle(StatusStyle.isLowerPlan);
      setDisplayMessage('選択不可');
      setModalContent('シングルページがある状態ではフリープランには変更できません。');
      setIsDeactivated(true);
      return;
    }

    // Reset free plan state when no single pages
    if (plan.name === 'フリープラン' && plan.isActivePlan) {
      setCardStyle(StatusStyle.isLowerPlan);
      setCardStyle(StatusStyle.isActivePlan);
      setDisplayMessage('このプランに変更する');
      setIsDeactivated(true);
      setModalContent('');
      setActivePlan(plan);
      return;
    }

    if (plan.isActivePlan && plan.name === 'ページ追加') {
      setActivePlan(plan);
      setIsDeactivated(false);
      setCardStyle(StatusStyle.isActivePlan);

      // Show status in Japanese if not active or trialing
      if (plan.stripe_status && plan.stripe_status !== 'active' && plan.stripe_status !== 'trialing') {
        const statusInJapanese = stripeStatusToJapanese[plan.stripe_status] || plan.stripe_status;
        setDisplayMessage(`利用中 (${statusInJapanese})`);
      } else {
        setDisplayMessage('利用中');
      }
    } else if (plan.isActivePlan && plan.name !== 'ページ追加') {
      setActivePlan(plan);
      setCardStyle(StatusStyle.isActivePlan);

      // Show status in Japanese if not active or trialing
      if (plan.stripe_status && plan.stripe_status !== 'active' && plan.stripe_status !== 'trialing') {
        const statusInJapanese = stripeStatusToJapanese[plan.stripe_status] || plan.stripe_status;
        setDisplayMessage(`利用中 (${statusInJapanese})`);
      } else {
        setDisplayMessage('利用中');
      }

      setIsDeactivated(true);
    } else if (plan.isLowerPlan) {
      setCardStyle(StatusStyle.isLowerPlan);
      setDisplayMessage('選択不可');
      setModalContent('制作済みページ数が制限を超えています。');
      setIsDeactivated(true);

      const minimumPagesToChange = parseInt(plan?.metadata?.feat_pages ?? 0, 10) + singlePagesCount;

      if (total <= minimumPagesToChange && plan.name !== 'フリープラン') {
        setCardStyle(StatusStyle.isNotLowerPlan);

        // Check if this is a single page plan
        if (plan.name === 'ページ追加' || plan.metadata?.type === 'singlePage') {
          if (plan.quantity_of_acquired_single_pages > 0) {
            setDisplayMessage('変更');
          } else {
            setDisplayMessage('追加する');
          }
        } else {
          setDisplayMessage('このプランに変更する');
        }

        setIsDeactivated(false);
      }
    } else {
      setCardStyle(StatusStyle.isNotLowerPlan);

      // Check if this is a single page plan
      if (plan.name === 'ページ追加' || plan.metadata?.type === 'singlePage') {
        if (plan.quantity_of_acquired_single_pages > 0) {
          setDisplayMessage('変更');
        } else {
          setDisplayMessage('追加する');
        }
      } else {
        setDisplayMessage('このプランに変更する');
      }
    }
  }, [singlePagesCount, plan.isActivePlan, user.is_invoice_mode_enabled, total, plan, invoicePlanId, setActivePlan, setSelectedPlan]);

  // functions
  const handleRegisterPlan = (plan: Plan) => {
    setSelectedPlan(plan);
    setTriedToRegisterToPlan(true);
  };

  const handleShowCantReturnModal = () => {
    if (isDeactivated && !plan.isActivePlan) setShowCantReturnModal(!showCantReturnModal);
  };

  // function displayPrice(): import('react').ReactNode {
  //   let price: React.ReactNode | string = (
  //     <>
  //       {plan.formattedPrice}
  //       <Typography
  //         position="relative"
  //         bottom={0}
  //         padding={1}
  //         fontSize={12}
  //         color="grey"
  //       >
  //         / 月(税込)
  //       </Typography>
  //     </>
  //   );

  //   if (user.is_invoice_mode_enabled && plan.id === invoicePlanId) {
  //     price = (
  //       <span
  //         style={{ fontSize: '0.8em' }}
  //       >
  //         お問い合わせ下さい
  //       </span>
  //     );
  //   }

  //   return (
  //     <>
  //       <Item
  //         sx={{
  //           display: 'flex',
  //           flexDirection: 'row',
  //           alignItems: 'center',
  //           justifyContent: 'center',
  //         }}
  //       >
  //         {price}
  //       </Item>
  //     </>
  //   );
  // }

  return (
    <Item
      sx={cardStyle}
    >
      <Item className="card-header">
        <p>{plan.description}</p>
        <FormLabel
          sx={{
            color: 'GrayText',
            fontSize: 12,
          }}
        >
          {plan.metadata?.additional_description}
        </FormLabel>
        <h3 className="text-bold my-2">{plan.name}</h3>
      </Item>
      <Item className="card-body">
        {/* <Item>
          <h1 className="text-bold">
            {displayPrice()}
          </h1>
        </Item> */}
        <Item sx={{ minHeight: 75 }}>
          {plan.features?.map((feature: any) => (
            <div key={uuid()}>
              {feature.name}
            </div>
          ))}

          {plan?.quantity_of_acquired_single_pages > 0 && (
            <div className="mt-3 py-1 px-3 text-primary text-bold border border-3 border-danger rounded-3 d-inline-block">
              {`現在 ${plan?.quantity_of_acquired_single_pages}ページ適用中`}
            </div>
          )}
        </Item>

        <Item onClick={handleShowCantReturnModal}>
          <Button
            disabled={isDeactivated}
            className="mt-2 w-100"
            variant="contained"
            onClick={
              customOnClickEventFor
                ? () => customOnClickEventFor(plan)
                : () => handleRegisterPlan(plan)
            }
          >
            {displayMessage}
          </Button>
        </Item>
      </Item>

      <ModalComponent
        title="プラン変更のお申込みはできません"
        show={showCantReturnModal}
        closeModal={handleShowCantReturnModal}
        onConfirm={handleShowCantReturnModal}
        onCloseModal={handleShowCantReturnModal}
        cancelButtonText="閉じる"
        hideConfirmButton
      >
        {modalContent}
      </ModalComponent>
    </Item>
  );
}

export default PlanCard;
